package com.oscarherreros.appcalculo.game

import kotlin.random.Random

/** [minSteps] es el mínimo de operaciones con el que se puede llegar a [target]. */
data class Level(val numbers: List<Int>, val target: Int, val minSteps: Int)

private class Difficulty(
    val smallCount: Int,
    val smallRange: IntRange,
    val largeCount: Int,
    val minSteps: Int,
    val targetRange: IntRange,
)

private val LARGE_NUMBERS = listOf(25, 50, 75, 100)

/** Cada dificultad y el nivel a partir del cual pasa a ser la habitual. */
private val MILESTONES = listOf(
    1 to Difficulty(4, 2..9, 0, minSteps = 1, targetRange = 5..20),
    7 to Difficulty(4, 2..9, 0, minSteps = 2, targetRange = 10..50),
    15 to Difficulty(4, 1..10, 0, minSteps = 3, targetRange = 20..100),
    25 to Difficulty(5, 1..10, 0, minSteps = 3, targetRange = 50..150),
    38 to Difficulty(4, 1..10, 1, minSteps = 3, targetRange = 100..300),
    53 to Difficulty(4, 1..10, 1, minSteps = 4, targetRange = 100..500),
    70 to Difficulty(3, 1..10, 2, minSteps = 4, targetRange = 200..999),
)

/**
 * La dificultad no salta de golpe de un hito al siguiente: entre los dos van
 * apareciendo niveles con la dificultad nueva, al principio sueltos y cada vez
 * más seguidos, hasta que al llegar al hito ya son todos.
 */
private fun difficultyFor(level: Int): Difficulty {
    val index = MILESTONES.indexOfLast { (start, _) -> start <= level }
    val (start, current) = MILESTONES[index]
    val (end, next) = MILESTONES.getOrNull(index + 1) ?: return current
    // Niveles adelantados acumulados hasta una posición: crecen con su cuadrado,
    // así que la frecuencia sube de forma lineal de 0 a 1 a lo largo del tramo.
    fun aheadUntil(position: Int) = position * position / (2 * (end - start))
    val position = level - start
    return if (aheadUntil(position + 1) > aheadUntil(position)) next else current
}

/** El nivel [number] de la campaña. Su semilla es el propio número, así que es siempre el mismo. */
fun generateLevel(number: Int): Level = generate(Random(number), difficultyFor(number))

/**
 * El reto diario del día [day], contado como en [DailyProgress]. Su semilla es
 * el día, así que es el mismo para todos los jugadores, con una de las tres
 * dificultades más altas.
 */
fun dailyLevel(day: Long): Level {
    val random = Random(day)
    val (_, difficulty) = MILESTONES.takeLast(3).random(random)
    return generate(random, difficulty)
}

/** El día del reto n.º 1: el 15 de octubre de 2026, cuando se publicó la web. */
const val FIRST_DAILY_DAY = 20_741L

/**
 * El número del reto diario de [day], el mismo en la app y en la web. Los días
 * anteriores al primero dan 0 o menos; solo se ven con el reloj mal puesto.
 */
fun dailyNumber(day: Long): Int = (day - FIRST_DAILY_DAY + 1).toInt()

/**
 * El nivel que toca en una partida contrarreloj cuando ya se han resuelto
 * [solved]. La dificultad avanza como en la campaña, dos niveles por cada uno
 * resuelto, pero con [random] los niveles son distintos en cada partida.
 */
fun timeAttackLevel(solved: Int, random: Random): Level = generate(random, difficultyFor(1 + 2 * solved))

/**
 * El objetivo se elige entre los valores alcanzables con los números
 * repartidos, por lo que el nivel siempre tiene solución.
 */
private fun generate(random: Random, difficulty: Difficulty): Level {
    while (true) {
        // Cada número pequeño puede salir como mucho dos veces.
        val small = (difficulty.smallRange + difficulty.smallRange).shuffled(random).take(difficulty.smallCount)
        val large = LARGE_NUMBERS.shuffled(random).take(difficulty.largeCount)
        val numbers = (small + large).sorted()
        val targets = reachableValues(numbers)
            .filter { (value, steps) -> steps == difficulty.minSteps && value in difficulty.targetRange }
            .keys
            .sorted()
        if (targets.isNotEmpty()) {
            return Level(numbers, targets.random(random).toInt(), difficulty.minSteps)
        }
    }
}
